'use client'

import { Loader2 } from 'lucide-react'
import { useState } from 'react'

import { ArticleDto } from '@/domains/articles/application/dto/getArticles.dto'
import ArticleCard from './ArticleCard'

interface ArticleListProps {
  initialArticles: ArticleDto[]
  total: number
  nextCursor?: string | null
}

export default function ArticleList({
  initialArticles,
  nextCursor: initialNextCursor,
}: ArticleListProps) {
  const [articles, setArticles] = useState<ArticleDto[]>(initialArticles)
  const [nextCursor, setNextCursor] = useState<string | null>(
    initialNextCursor || null
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadMore = async () => {
    if (!nextCursor || isLoading) return

    setIsLoading(true)
    setError(null)
    try {
      const response = await fetch(`/api/articles?cursor=${nextCursor}`)
      if (!response.ok) throw new Error(String(response.status))

      const { data } = await response.json()
      setArticles((prev) => [...prev, ...data.articles])
      setNextCursor(data.nextCursor)
    } catch (err) {
      console.error('Erreur lors du chargement des articles:', err)
      setError("Les articles suivants n'ont pas pu être chargés. Réessayez.")
    } finally {
      setIsLoading(false)
    }
  }

  if (articles.length === 0) {
    return (
      <p className="border-t border-ink/15 pt-8 text-anthracite">
        Aucun article disponible pour le moment.
      </p>
    )
  }

  return (
    <div>
      <div className="border-t border-ink/15">
        {articles.map((article, index) => (
          <ArticleCard key={`${article.id}-${index}`} {...article} />
        ))}
      </div>

      {error && (
        <p
          role="status"
          className="mt-8 font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-signal-deep"
        >
          {error}
        </p>
      )}

      {nextCursor && (
        <div className="mt-12">
          <button
            type="button"
            onClick={loadMore}
            disabled={isLoading}
            className="inline-flex items-center gap-3 rounded-[3px] border border-ink/25 px-7 py-4 font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-chalk disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading && (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            )}
            <span>Plus d&apos;articles</span>
          </button>
        </div>
      )}
    </div>
  )
}
